import LFCDataGrid from '_components/datagrid/LFCDataGrid';
import {ProgressAria} from '_components/ProgressAria';
import {fmtYMD} from '_logics/LFCFormatUtil';
import {rendJudgeDisp, rendTotalJudgeDisp} from '_logics/LFCRenderUtil';
import {getLFCDataProc} from '_logics/LFCUtil';
import axios, {CancelTokenSource} from 'axios';
import {isEmpty} from 'lodash';
import {useSnackbar} from 'notistack';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Grid} from '@mui/material';
import {
  GridAlignment,
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridValueFormatterParams
} from '@mui/x-data-grid-pro';

import {DetailDialog} from './components/detail-dialog';
import {SearchConditions} from './types';

// ヘッダータグ定数
const HEADER_TAG_MAIN = '[main]';
const HEADER_TAG_ADD = '[add]';
const HEADER_TAG_MAP = '[map]';
const HEADER_TAG_REGEX = /\[.*?\]/g; // タグ除去用の正規表現

// カラム設定の統一定義
interface BaseColumnConfig {
  field: string;
  headerNameKey: string;
  width: number;
  exportOrder: number; // ファイル出力時の順序
  align?: GridAlignment;
  disableColumnMenu?: boolean;
  valueFormatter?: (prms: GridValueFormatterParams) => any;
  renderCell?: (prms: GridRenderCellParams) => any;
}

const BASE_COLUMNS_CONFIG: BaseColumnConfig[] = [
  {
    field: 'lq_time',
    headerNameKey: '検査日時',
    width: 140,
    exportOrder: 1,
    valueFormatter: (prms: GridValueFormatterParams) => fmtYMD(prms.value)
  },
  {
    field: 'total_judge',
    headerNameKey: '総合判定',
    width: 80,
    exportOrder: 5,
    align: 'center',
    disableColumnMenu: true,
    renderCell: (prms: GridRenderCellParams) => rendTotalJudgeDisp(prms.value)
  },
  {
    field: 'serial',
    headerNameKey: 'シリアルNo',
    width: 140,
    exportOrder: 2
  },
  {
    field: 'work',
    headerNameKey: '機種',
    width: 100,
    exportOrder: 3
  },
  {
    field: 'deviceid',
    headerNameKey: '検査装置',
    width: 100,
    exportOrder: 4
  },
  {
    field: 'user_name',
    headerNameKey: 'オペレータ',
    width: 100,
    exportOrder: 6
  }
];

// 型定義
interface DataRow {
  [key: string]: any;
  id: number;
}

interface ColumnOrderItem {
  name: string;
  order: number;
}

type ApiResponseData = any[][]; // CSV形式のデータ

interface MainPanelProps {
  searchConditions: SearchConditions | null;
}

export const MainPanel = ({searchConditions}: MainPanelProps) => {
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const [startProcess, setStartProcess] = useState<boolean>(false);
  const cancelSource = useRef<CancelTokenSource | null>(null);

  const [dgDs, setDgDs] = useState<ApiResponseData>([]);
  const [dgColumns, setDgColumns] = useState<GridColDef[]>([]);
  const [dgRows, setDgRows] = useState<DataRow[]>([]);

  const [open, setOpen] = useState(false);
  const [openUuid, setOpenUuid] = useState('');

  /**
   * 初期カラム定義
   */
  const BASE_COL: GridColDef[] = useMemo(
    () =>
      BASE_COLUMNS_CONFIG.map(config => ({
        field: `${HEADER_TAG_MAIN}${config.field}`,
        headerName: t(config.headerNameKey),
        description: t(config.headerNameKey),
        width: config.width,
        ...(config.align && {align: config.align}),
        ...(config.disableColumnMenu && {disableColumnMenu: config.disableColumnMenu}),
        ...(config.valueFormatter && {valueFormatter: config.valueFormatter}),
        ...(config.renderCell && {renderCell: config.renderCell})
      })),
    [t]
  );

  const COLUMN_ORDER: ColumnOrderItem[] = useMemo(
    () =>
      BASE_COLUMNS_CONFIG.map(config => ({
        name: `${HEADER_TAG_MAIN}${config.field}`,
        order: config.exportOrder
      })),
    []
  );

  /**
   * 明細選択
   */
  const handleCellClick = (params: GridCellParams) => {
    setOpenUuid(params.row[`${HEADER_TAG_MAIN}uuid`]);
    setOpen(true);
  };

  /**
   * 初期処理
   */
  useEffect(() => {
    return () => {
      // アンマウント処理
      if (cancelSource.current) cancelSource.current.cancel('req cancel');
    };
  }, []);

  /**
   * 検索処理
   */
  useEffect(() => {
    if (searchConditions === null) return;

    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    setDgDs([]);

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      getLFCDataProc({
        snack: enqueueSnackbar,
        //sql_id: 6702,
        invoke_name: 'LWAppResultSearch',
        parameters: {
          serial: searchConditions.serialno === '' ? null : `%${searchConditions.serialno}%`,
          date1: searchConditions.selectDateFrom === '' ? null : searchConditions.selectDateFrom,
          date2: searchConditions.selectDateTo === '' ? null : searchConditions.selectDateTo,
          judge: searchConditions.judge === 9 ? null : searchConditions.judge,
          work: isEmpty(searchConditions.work) ? null : '{' + searchConditions.work.join(',') + '}'
        },
        ds_state: setDgDs,
        name: `${t('検索結果')}`,
        cancelToken: cancelSource.current.token,
        t
      })
    ]).finally(() => {
      setStartProcess(false);
    });
  }, [searchConditions]);

  /**
   * 動的カラム定義を作成
   */
  const createDynamicColumns = useCallback((headers: string[]): GridColDef[] => {
    const judge_align: GridAlignment = 'center';

    const {addColumns, mapColumns} = headers.reduce(
      (acc, header) => {
        if (!header) {
          return acc;
        }

        const cleanHeader = header.replace(HEADER_TAG_REGEX, '');

        if (header.includes(HEADER_TAG_ADD)) {
          acc.addColumns.push({
            field: header,
            headerName: cleanHeader,
            width: 100
          });
        } else if (header.includes(HEADER_TAG_MAP)) {
          acc.mapColumns.push({
            field: header,
            headerName: cleanHeader,
            width: 100,
            align: judge_align,
            headerAlign: judge_align,
            disableColumnMenu: true,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => rendJudgeDisp(params.value)
          });
        }
        return acc;
      },
      {addColumns: [] as GridColDef[], mapColumns: [] as GridColDef[]}
    );

    // ADDカラムを先に、その後MAPカラムを追加
    return [...addColumns, ...mapColumns];
  }, []);

  /**
   * CSV形式データをJSON形式に変換
   */
  const convertCsvToJson = useCallback((headers: string[], dataRows: any[]): DataRow[] => {
    return dataRows.map((values: any[], rowIndex: number) => {
      const rowData = headers.reduce(
        (acc: any, key: string, colIndex: number) => {
          // keyが存在し、空文字でない場合のみプロパティを追加
          if (key && key.trim() !== '') {
            acc[key] = values[colIndex];
          }
          return acc;
        },
        {id: rowIndex}
      );
      return rowData;
    });
  }, []);

  /**
   * レスポンスデータ取得後に明細を展開する
   */
  useEffect(() => {
    // データが空の場合は初期化
    if (dgDs.length === 0) {
      setDgColumns([]);
      setDgRows([]);
      return;
    }

    // 最初の行をヘッダーとして取得（配列全体のコピーを避ける）
    const headers: string[] = dgDs[0] || [];

    // データ行を取得（必要な部分のみスライス）
    const dataRows = dgDs.slice(1);

    // カラム定義を設定（固定カラム + 動的カラム）
    const dynamicColumns = createDynamicColumns(headers);
    setDgColumns([...BASE_COL, ...dynamicColumns]);

    // データ行をJSON形式に変換
    const jsonData = convertCsvToJson(headers, dataRows);
    setDgRows(jsonData);
  }, [dgDs, BASE_COL, createDynamicColumns, convertCsvToJson]);

  // DataGridの初期状態をメモ化
  const dataGridInitialState = useMemo(
    () => ({
      left: [`${HEADER_TAG_MAIN}lq_time`, `${HEADER_TAG_MAIN}total_judge`],
      right: []
    }),
    []
  );

  return (
    <ProgressAria startProcess={startProcess} loadingMessage>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <LFCDataGrid
            columns={dgColumns}
            rows={dgRows}
            height="80vh"
            onCellClick={handleCellClick}
            exportFilename={`${t('検査結果検索')}`}
            columnOrder={COLUMN_ORDER}
            style={{cursor: 'pointer'}}
            initialState={dataGridInitialState}
            pageSize={100}
          />
        </Grid>
      </Grid>
      <DetailDialog open={open} onClose={() => setOpen(false)} uuid={openUuid} />
    </ProgressAria>
  );
};
